import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Users, ChevronDown, Search, X, RotateCcw, Save, Loader2, CheckSquare, Square, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getTabDefinitions,
  getRolePermissions,
  saveRolePermissions,
  getUserPermissions,
  saveUserPermissions,
  clearUserPermissions,
} from '../../api/tabPermissionApi';
import { getUserLookup } from '../../api/adminApi';
import { TabPermissionsSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';

const ROLE_NAMES = ['User', 'Employee', 'Lead', 'PM', 'Admin'];

const TabPermissionsPage = () => {
  // ── State ──
  const [tabs, setTabs] = useState([]);
  const [roleMatrix, setRoleMatrix] = useState({});       // { "tabKey|role": bool }
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userOverrides, setUserOverrides] = useState({});  // { tabKey: bool }
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [loadingTabs, setLoadingTabs] = useState(true);
  const [savingRoles, setSavingRoles] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [activeTab, setActiveTab] = useState('roles'); // 'roles' | 'users'

  // ── Load tab definitions + role matrix + user list on mount ──
  useEffect(() => {
    (async () => {
      try {
        const [tabRes, roleRes, userRes] = await Promise.all([
          getTabDefinitions(),
          getRolePermissions(),
          getUserLookup(),
        ]);

        const tabList = tabRes?.data ?? tabRes ?? [];
        setTabs(tabList);

        // Build matrix from role permissions
        const matrix = {};
        const perms = roleRes?.data ?? roleRes ?? [];
        perms.forEach(p => {
          matrix[`${p.tabKey}|${p.role}`] = p.isVisible;
        });
        setRoleMatrix(matrix);

        const userList = userRes?.data ?? userRes ?? [];
        setUsers(userList);
      } catch (err) {
        console.error('Failed to load tab permissions:', err);
        toast.error('Failed to load tab permissions');
      } finally {
        setLoadingTabs(false);
      }
    })();
  }, []);

  // ── Role matrix helpers ──
  const getRoleValue = (tabKey, roleIdx) => {
    const key = `${tabKey}|${roleIdx}`;
    return roleMatrix[key] ?? true; // default visible
  };

  const toggleRole = (tabKey, roleIdx) => {
    const key = `${tabKey}|${roleIdx}`;
    setRoleMatrix(prev => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  };

  const handleSaveRoles = async () => {
    setSavingRoles(true);
    try {
      // Build flat array of all cells in the matrix
      const permissions = [];
      tabs.forEach(tab => {
        ROLE_NAMES.forEach((_, idx) => {
          permissions.push({
            tabKey: tab.tabKey,
            role: idx,
            isVisible: getRoleValue(tab.tabKey, idx),
          });
        });
      });

      await saveRolePermissions(permissions);
      toast.success('Role permissions saved');
    } catch (err) {
      console.error('Save role permissions failed:', err);
      toast.error('Failed to save role permissions');
    } finally {
      setSavingRoles(false);
    }
  };

  // ── User override helpers ──
  const loadUserOverrides = useCallback(async (userId) => {
    if (!userId) {
      setUserOverrides({});
      return;
    }
    setLoadingUser(true);
    try {
      const res = await getUserPermissions(userId);
      const perms = res?.data ?? res ?? [];
      const map = {};
      perms.forEach(p => { map[p.tabKey] = p.isVisible; });
      setUserOverrides(map);
    } catch (err) {
      console.error('Failed to load user permissions:', err);
      toast.error('Failed to load user permissions');
    } finally {
      setLoadingUser(false);
    }
  }, []);

  const selectUser = (user) => {
    setSelectedUserId(user.id);
    setUserSearch(user.name);
    setShowUserDropdown(false);
    loadUserOverrides(user.id);
  };

  const getUserValue = (tabKey) => {
    if (tabKey in userOverrides) return userOverrides[tabKey];
    return null; // no override (inherit from role)
  };

  const cycleUserValue = (tabKey) => {
    const current = getUserValue(tabKey);
    setUserOverrides(prev => {
      const copy = { ...prev };
      if (current === null) {
        // No override → set to hidden
        copy[tabKey] = false;
      } else if (current === false) {
        // Hidden → set to visible
        copy[tabKey] = true;
      } else {
        // Visible → remove override (inherit)
        delete copy[tabKey];
      }
      return copy;
    });
  };

  const handleSaveUser = async () => {
    if (!selectedUserId) return;
    setSavingUser(true);
    try {
      const permissions = Object.entries(userOverrides).map(([tabKey, isVisible]) => ({
        tabKey,
        userId: selectedUserId,
        isVisible,
      }));

      if (permissions.length === 0) {
        await clearUserPermissions(selectedUserId);
      } else {
        await saveUserPermissions(selectedUserId, permissions);
      }
      toast.success('User permissions saved');
    } catch (err) {
      console.error('Save user permissions failed:', err);
      toast.error('Failed to save user permissions');
    } finally {
      setSavingUser(false);
    }
  };

  const handleClearUser = async () => {
    if (!selectedUserId) return;
    setSavingUser(true);
    try {
      await clearUserPermissions(selectedUserId);
      setUserOverrides({});
      toast.success('User overrides cleared');
    } catch (err) {
      toast.error('Failed to clear overrides');
    } finally {
      setSavingUser(false);
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // ── Group tabs ──
  const grouped = tabs.reduce((acc, tab) => {
    const g = tab.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(tab);
    return acc;
  }, {});

  // ── Render ──
  if (loadingTabs) {
    return <TabPermissionsSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <PageFadeSection>
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-operis-blue to-indigo-600 flex items-center justify-center shadow-md">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tab Permissions</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Control which tabs each role or user can see</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200
            ${activeTab === 'roles'
              ? 'bg-white dark:bg-gray-700 text-operis-blue shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
        >
          <Shield className="w-4 h-4 inline mr-2 -mt-0.5" />
          Role Permissions
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200
            ${activeTab === 'users'
              ? 'bg-white dark:bg-gray-700 text-operis-blue shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
        >
          <Users className="w-4 h-4 inline mr-2 -mt-0.5" />
          User Overrides
        </button>
      </div>

      {/* ═════════ Role Permissions Tab ═════════ */}
      {activeTab === 'roles' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Role Permission Matrix</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Check / uncheck to allow or hide tabs for each role. All tabs are visible by default.
              </p>
            </div>
            <button
              onClick={handleSaveRoles}
              disabled={savingRoles}
              className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-operis-blue to-indigo-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-operis-blue/25 transition-all disabled:opacity-50"
            >
              {savingRoles ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-56">
                    Tab
                  </th>
                  {ROLE_NAMES.map(name => (
                    <th key={name} className="text-center px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {Object.entries(grouped).map(([group, groupTabs]) => (
                  <React.Fragment key={group}>
                    <tr>
                      <td
                        colSpan={ROLE_NAMES.length + 1}
                        className="px-6 py-2 bg-indigo-50/50 dark:bg-indigo-500/5 text-xs font-bold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider"
                      >
                        {group}
                      </td>
                    </tr>
                    {groupTabs.map(tab => (
                      <tr key={tab.tabKey} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                        <td className="px-6 py-3 font-medium text-gray-800 dark:text-gray-200">
                          {tab.label}
                        </td>
                        {ROLE_NAMES.map((_, idx) => {
                          const checked = getRoleValue(tab.tabKey, idx);
                          return (
                            <td key={idx} className="text-center px-4 py-3">
                              <button
                                onClick={() => toggleRole(tab.tabKey, idx)}
                                className="mx-auto block group"
                              >
                                {checked ? (
                                  <CheckSquare className="w-5 h-5 text-green-500 group-hover:text-green-600 transition-colors" />
                                ) : (
                                  <Square className="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 transition-colors" />
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═════════ User Overrides Tab ═════════ */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              User overrides take priority over role permissions. Click to cycle through:
              <span className="inline-flex items-center gap-1 ml-2">
                <CheckSquare className="w-4 h-4 text-green-500 inline" /> Visible
              </span>
              <span className="inline-flex items-center gap-1 ml-2">
                <Square className="w-4 h-4 text-red-400 inline" /> Hidden
              </span>
              <span className="inline-flex items-center gap-1 ml-2">
                <span className="w-4 h-4 inline-block border-2 border-dashed border-gray-300 rounded" /> Inherit from role
              </span>
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center gap-3">
              {/* User search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search user..."
                  value={userSearch}
                  onChange={e => {
                    setUserSearch(e.target.value);
                    setShowUserDropdown(true);
                    if (!e.target.value) {
                      setSelectedUserId('');
                      setUserOverrides({});
                    }
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  className="w-full pl-9 pr-9 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-operis-blue/40 focus:border-operis-blue transition-all"
                />
                {userSearch && (
                  <button
                    onClick={() => {
                      setUserSearch('');
                      setSelectedUserId('');
                      setUserOverrides({});
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
                {showUserDropdown && userSearch && filteredUsers.length > 0 && (
                  <div className="absolute z-50 top-full mt-1 left-0 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                    {filteredUsers.map(u => (
                      <button
                        key={u.id}
                        onClick={() => selectUser(u)}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors
                          ${u.id === selectedUserId ? 'bg-indigo-50 dark:bg-indigo-500/10 text-operis-blue font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                      >
                        {u.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleClearUser}
                  disabled={!selectedUserId || savingUser}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all disabled:opacity-40"
                >
                  <RotateCcw className="w-4 h-4" />
                  Clear Overrides
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={!selectedUserId || savingUser}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-operis-blue to-indigo-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-operis-blue/25 transition-all disabled:opacity-50"
                >
                  {savingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </div>

            {/* User tab overrides list */}
            {selectedUserId ? (
              loadingUser ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-operis-blue" />
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {Object.entries(grouped).map(([group, groupTabs]) => (
                    <React.Fragment key={group}>
                      <div className="px-6 py-2 bg-indigo-50/50 dark:bg-indigo-500/5 text-xs font-bold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider">
                        {group}
                      </div>
                      {groupTabs.map(tab => {
                        const val = getUserValue(tab.tabKey);
                        return (
                          <div
                            key={tab.tabKey}
                            className="flex items-center justify-between px-6 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors"
                          >
                            <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">{tab.label}</span>
                            <button onClick={() => cycleUserValue(tab.tabKey)} className="group flex items-center gap-2">
                              {val === null ? (
                                <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                                  <span className="w-5 h-5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded" />
                                  Inherit
                                </span>
                              ) : val ? (
                                <span className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                                  <CheckSquare className="w-5 h-5" />
                                  Visible
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-xs text-red-500 dark:text-red-400">
                                  <Square className="w-5 h-5" />
                                  Hidden
                                </span>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
                <Users className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm font-medium">Select a user to manage their tab overrides</p>
              </div>
            )}
          </div>
        </div>
      )}
      </PageFadeSection>
    </div>
  );
};

export default TabPermissionsPage;
